import {
  TrendingUp,
  Megaphone,
  Share2,
  Target,
  Hotel,
  Cpu,
} from "lucide-react";

const IMG = {
  sand:
    "https://images.unsplash.com/photo-1708748513828-2227f6d39c04?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwxfHxwcmVtaXVtJTIwYWJzdHJhY3QlMjBzYW5kJTIwZHVuZXN8ZW58MHx8fHwxNzg1ODQxMjI0fDA&ixlib=rb-4.1.0&q=85",
  hotel:
    "https://images.unsplash.com/photo-1677129667171-92abd8740fa3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBob3RlbCUyMGxvdW5nZSUyMGhvc3BpdGFsaXR5fGVufDB8fHx8MTc4NTg0MTIyNHww&ixlib=rb-4.1.0&q=85",
  office:
    "https://images.unsplash.com/photo-1758518731706-be5d5230e5a5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBjb3Jwb3JhdGUlMjBvZmZpY2UlMjBtZWV0aW5nfGVufDB8fHx8MTc4NTg0MTIyNHww&ixlib=rb-4.1.0&q=85",
  city:
    "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxkdWJhaSUyMGNpdHlzY2FwZSUyMGFyY2hpdGVjdHVyZSUyMG1vZGVybnxlbnwwfHx8fDE3ODU4NDEyMjR8MA&ixlib=rb-4.1.0&q=85",
  advertising:
    "https://images.unsplash.com/photo-1552664730-d307ca884978?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  onlineAd:
    "https://images.unsplash.com/photo-1460925895917-afdab827c52f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  onlineMkt:
    "https://images.unsplash.com/photo-1611926653458-09294b3142bf?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  tech:
    "https://images.unsplash.com/photo-1518770660439-4636190af475?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
};

export const SERVICES = [
  {
    slug: "digital-marketing",
    num: "01",
    title: "Digital Marketing",
    icon: TrendingUp,
    image: IMG.sand,
    short:
      "Full-funnel strategy, SEO, content and performance campaigns engineered for measurable growth.",
    tagline: "Growth, engineered.",
    overview: [
      "We build data-driven digital marketing programmes that turn attention into revenue. From search visibility to conversion, every tactic is measured against a real business outcome.",
      "Our specialists combine strategy, creative and analytics to help brands worldwide scale predictably and profitably.",
    ],
    offerings: [
      { title: "Search Engine Optimisation (SEO)", desc: "Technical, on-page and content SEO to grow organic visibility and qualified traffic." },
      { title: "Content Marketing", desc: "Editorial strategy, articles, video and assets that build authority and demand." },
      { title: "Performance Campaigns", desc: "Full-funnel campaigns optimised continuously against ROAS and lead quality." },
      { title: "Marketing Analytics", desc: "Dashboards, attribution and reporting so you always know what's working." },
    ],
    span: "md:col-span-8",
    tall: true,
    cardImage: true,
  },
  {
    slug: "hospitality",
    num: "02",
    title: "Hospitality Services",
    icon: Hotel,
    image: IMG.hotel,
    short: "Bespoke solutions for hotels, events and premium guest experiences.",
    tagline: "Experiences worth remembering.",
    overview: [
      "We help hospitality brands and private clients deliver world-class guest experiences — from concept to concierge.",
      "Whether you operate a hotel, host events or need premium arrangements in the UAE, our team manages every detail with discretion.",
    ],
    offerings: [
      { title: "Hotel & Venue Partnerships", desc: "Preferred rates and curated stays across the UAE's finest properties." },
      { title: "Event Management", desc: "End-to-end planning for corporate and private events, launches and galas." },
      { title: "Guest Experience & Concierge", desc: "Bespoke itineraries, transfers, dining and VIP arrangements." },
      { title: "F&B Consulting", desc: "Concept, menu and service design for restaurants and lounges." },
    ],
    span: "md:col-span-4",
    tall: true,
    cardImage: true,
  },
  {
    slug: "advertising",
    num: "03",
    title: "Advertising",
    icon: Megaphone,
    image: IMG.advertising,
    short: "Brand-defining creative and media buying across print, OOH and broadcast.",
    tagline: "Impossible to ignore.",
    overview: [
      "We craft advertising that defines brands and commands attention — from the creative idea to the media that carries it.",
      "Our team plans and buys across traditional and outdoor channels to put your message in front of the right audience at scale.",
    ],
    offerings: [
      { title: "Creative & Art Direction", desc: "Concepts, campaigns and brand-defining creative that stand out." },
      { title: "Out-of-Home (OOH)", desc: "Billboards and premium outdoor placements across key UAE locations." },
      { title: "Print & Broadcast", desc: "Magazine, TV and radio campaigns with full production support." },
      { title: "Media Planning & Buying", desc: "Strategic buying negotiated for maximum reach and value." },
    ],
    span: "md:col-span-4",
    tall: false,
    cardImage: true,
  },
  {
    slug: "online-advertising",
    num: "04",
    title: "Online Advertising",
    icon: Target,
    image: IMG.onlineAd,
    short: "Paid search, programmatic and social ads managed for maximum ROI.",
    tagline: "Every dirham accountable.",
    overview: [
      "We run paid advertising that delivers efficient, trackable results across every major platform.",
      "From first click to conversion, campaigns are structured, tested and optimised to lower cost and lift returns.",
    ],
    offerings: [
      { title: "Google & Search Ads", desc: "High-intent search campaigns that capture demand as it happens." },
      { title: "Meta & Social Ads", desc: "Instagram, Facebook, TikTok and LinkedIn campaigns that scale." },
      { title: "Programmatic & Display", desc: "Automated buying and retargeting across premium inventory." },
      { title: "YouTube & Video", desc: "Video campaigns that build awareness and drive action." },
    ],
    span: "md:col-span-4",
    tall: false,
    cardImage: true,
  },
  {
    slug: "online-marketing",
    num: "05",
    title: "Online Marketing",
    icon: Share2,
    image: IMG.onlineMkt,
    short: "Social, email and funnel automation that turns attention into loyalty.",
    tagline: "Turn audiences into advocates.",
    overview: [
      "We build the systems that nurture your audience — social, email and automation working together to grow loyalty and lifetime value.",
      "From content calendars to conversion funnels, we keep your brand consistently top of mind.",
    ],
    offerings: [
      { title: "Social Media Management", desc: "Content, community and growth across your key platforms." },
      { title: "Email & CRM Automation", desc: "Lifecycle journeys that convert and retain customers." },
      { title: "Funnel & Landing Pages", desc: "Optimised funnels engineered for conversion." },
      { title: "Influencer Marketing", desc: "Curated partnerships with creators relevant to your audience." },
    ],
    span: "md:col-span-4",
    tall: false,
    cardImage: true,
  },
  {
    slug: "it-consulting",
    num: "06",
    title: "IT Consulting",
    icon: Cpu,
    image: IMG.tech,
    short: "Networks, systems and IT consulting engineered for reliable, secure growth.",
    tagline: "Technology, done right.",
    overview: [
      "We help businesses worldwide architect, secure and modernise their technology — from networks and computer systems to full IT strategy.",
      "Our consultants combine engineering discipline with commercial insight to make technology a competitive advantage, not a distraction.",
    ],
    offerings: [
      { title: "Network Consultancies", desc: "Design, deployment and optimisation of enterprise networks, connectivity and secure infrastructure." },
      { title: "Computer Systems Consultancies", desc: "Assessment, architecture and implementation of business computer systems and platforms." },
      { title: "Information Technology Consulting", desc: "IT strategy, technology audits and transformation roadmaps tailored to your business." },
      { title: "Digital Transformation Advisory", desc: "Cloud, security and modernisation programs guided end-to-end for clients worldwide." },
    ],
    span: "md:col-span-12",
    tall: true,
    cardImage: true,
  },
];

export const getService = (slug) => SERVICES.find((s) => s.slug === slug);
